import React, { FC, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import styled from '@emotion/styled';
import { captureException } from 'src/shared/helpers/captureException';
import { useTranslation } from 'react-i18next';
import { filterNullish } from '@modernhealth/filter-nullish';
import { layout } from '@modernhealth/vital-foundation';
import {
    ContentCardsRowSkeleton,
    PageHeader,
    PageHeaderSkeleton,
    SkeletonPlaceholder,
    SectionHeaderSkeleton,
    Button,
    pxToRem,
} from '@modernhealth/vital-components';

import { appClient } from 'src/config/apollo';
import { RenderScreenSectionNode } from 'src/modules/dashboard/components/ContentCard';
import { ErrorFallback } from 'src/modules/dashboard/components/ErrorFallback';

import { useGetScreenBySlugQuery } from '../documents/getScreenBySlug.generated';

export enum MediaTypePageTestID {
    ContentWrapper = 'MediaTypePage.ContentWrapper',
}

type Props = RouteComponentProps<{ name?: string }, {}, { name?: string }>;

export const MediaTypePage: FC<Props> = ({ location, history, match }) => {
    const mediaTypeName = match.params?.name || location.state?.name || '';
    const { t } = useTranslation();

    const { data, loading, error } = useGetScreenBySlugQuery({
        client: appClient,
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
        onError: captureException,
        variables: {
            slug: mediaTypeName,
        },
        skip: !mediaTypeName,
    });

    useEffect(() => {
        if (!loading && !error && !data?.screenBySlug) {
            captureException(
                new Error('Missing required data in the response to render media type page'),
                { response: JSON.stringify(data) },
            );
        }
    }, [data, loading, error]);

    const mediaTypeScreen = data?.screenBySlug;

    if (loading && !mediaTypeScreen) {
        return (
            <Wrapper>
                <PageHeaderSkeleton />
                <SkeletonPlaceholder width="100%" height={250} borderRadius={8} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
            </Wrapper>
        );
    }

    if (!mediaTypeScreen) {
        return <ErrorFallback />;
    }

    return (
        <Wrapper data-testid={MediaTypePageTestID.ContentWrapper}>
            <Button
                variant="flat"
                size="medium"
                icon="arrow-left"
                iconAlign="start"
                onClick={() => {
                    history.goBack();
                }}
                css={{ paddingLeft: 0, marginTop: pxToRem(4) }}
            >
                {t('dashboard:button_back')}
            </Button>

            <PageHeader>{mediaTypeScreen.name}</PageHeader>

            {filterNullish(mediaTypeScreen.screenSections).map(section => (
                <RenderScreenSectionNode section={section} key={section.id} />
            ))}
        </Wrapper>
    );
};

const Wrapper = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
});
